import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { SetFormActionWithId } from '@app/finxone-web-frontend/app/lib/actions/form-submission.action';
import { AccountService } from '@app/finxone-web-frontend/app/lib/services/account-service/account-service.service';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { CountriesCurrenciesService } from '@app/finxone-web-frontend/app/lib/services/countries-currencies-service/countries-currencies.service';
import { findAndShowAlertFromCode } from '@app/finxone-web-frontend/app/lib/utils/alert-code-utils/alert-code.utils';
import { updateBiometricsFlagInStorage } from '@app/finxone-web-frontend/app/lib/utils/cta-button-actions/profile-zone/toggle-biometric-authentication.utils';
import {
  buildHandleBarModel,
  filterIfHandlebarNotRendered,
} from '@app/finxone-web-frontend/app/lib/utils/template/model-builder.utils';
import { getNavigationUrl } from '@app/finxone-web-frontend/app/lib/utils/utils';
import { ProfileErrorCodes } from '@app/translations';
import { FormActionTypeEnum } from '@finxone-platform/form-action';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { AlertHandlerService, IconCacheService } from '@finxone-platform/shared/services';
import {
  APP_ZONES,
  BaseCssProperties,
  BaseWidgetProperties,
  NavigationOptionEnum,
  ProviderClass,
  UiZoneWidgetAttributeConfig,
  baseCssConfigStyle,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import Handlebars from 'handlebars';
import { Subject, takeUntil } from 'rxjs';
import { BaseComponent } from '../../base/base.component';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-biometrics-authentication-widget',
  templateUrl: './biometrics-authentication-widget.component.html',
  styleUrl: './biometrics-authentication-widget.component.scss',
})
export class BiometricsAuthenticationWidgetComponent extends BaseComponent implements OnInit, OnDestroy {
  @AutoDestroy private destroy$: Subject<void> = new Subject<void>();
  public isChecked: boolean;
  private flutterWebView = (window as any).flutter_inappwebview;
  private appPreferences: string | null;
  public iconSize?: baseCssConfigStyle;
  public textContentStyle: baseCssConfigStyle;
  public widgetConfigData!: BaseWidgetProperties;
  private isConfirmationScreenActive: boolean;
  private content = '';
  public renderedContent = '';

  constructor(
    private configService: ConfigService,
    private alertService: AlertHandlerService,
    private readonly store: Store,
    private readonly router: Router,
    public dataIn: ProviderClass<UiZoneWidgetAttributeConfig>,
    private countriesCurrenciesService: CountriesCurrenciesService,
    private iconCacheService: IconCacheService,
    private accountService: AccountService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.listenForBiometricsSetupResult();
    this.appPreferences = localStorage.getItem('APP_PREFERENCES');
    if (this.appPreferences) {
      const parsedPreferences = JSON.parse(this.appPreferences);
      this.isChecked = parsedPreferences.isBiometricEnabled;
    } else {
      this.isChecked = false;
    }
    if (this.dataIn) {
      this.attri = this.dataIn.attri ?? null;
      this.onLoadWidgetConfiguration();
      if (this.attri !== null) {
        this.widgetConfigData = this.dataIn.attri?.widgetProperties;
      }
    }
    this.onLoadWidgetContent();
  }

  private onLoadWidgetContent(): void {
    this.content = this.dataIn?.attri?.widgetProperties?.textContent ?? '';
    const template = Handlebars.compile(this.content);
    buildHandleBarModel(
      this.store,
      this.content,
      this.countriesCurrenciesService,
      this.iconCacheService,
      this.accountService,
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe((model) => {
        this.renderedContent = filterIfHandlebarNotRendered(template(model));
      });
  }

  ngOnDestroy() {
    window.removeEventListener('biometricsPreferenceState', this.reset);
  }

  private onLoadWidgetConfiguration(): void {
    this.contentStyle = this.configService.getSpecificBaseCssStyle(this.attri, [
      BaseCssProperties.MARGIN,
      BaseCssProperties.PADDING,
      BaseCssProperties.BACKGROUND_COLOR,
      BaseCssProperties.BORDER,
      BaseCssProperties.BOX_SHADOW,
      BaseCssProperties.COLOR,
      BaseCssProperties.V_ALIGN,
      BaseCssProperties.TEXTALIGN,
    ]);

    this.iconSize = this.configService.getSpecificBaseCssStyle(this.attri, [BaseCssProperties.ICON_SIZE]);

    this.textContentStyle = this.configService.getSpecificBaseCssStyle(this.attri, [
      BaseCssProperties.COLOR,
      BaseCssProperties.FONT_SIZE,
      BaseCssProperties.TEXT_FONT_STYLE,
    ]);
    this.isConfirmationScreenActive = this.attri?.elementsConfig?.[0]?.show ?? false;
  }

  public onChecked(checked: boolean) {
    if (this.isConfirmationScreenActive) {
      this.updateFormStateAndRedirectToConfirmationScreen(checked);
    } else {
      this.updateBiometricsSettings(checked);
    }
  }

  private updateBiometricsSettings(checked: boolean) {
    this.isChecked = checked;
    updateBiometricsFlagInStorage(checked);
    this.updateFlutterWebView(this.isChecked);
    const alertCode = this.isChecked
      ? ProfileErrorCodes.BIOMETRIC_AUTH_ENABLED_SUCCESSFULLY
      : ProfileErrorCodes.BIOMETRIC_AUTH_DISABLED_SUCCESSFULLY;
    findAndShowAlertFromCode(this.store, this.router, this.alertService, [alertCode]);
  }

  private updateFormStateAndRedirectToConfirmationScreen(checked: boolean) {
    this.store.dispatch(
      new SetFormActionWithId(
        {
          type: '',
          formData: {
            prevValue: this.isChecked,
            currentValue: checked,
          },
        },
        FormActionTypeEnum.BIOMETRICS_AUTHENTICATION_TOGGLE,
      ),
    );

    const redirectUrl = getNavigationUrl(this.dataIn.attri);
    if (redirectUrl && redirectUrl === NavigationOptionEnum.USE_AS_BLANK) return;

    // Construct the URL based on the checked state.
    // If checked (checked = true), navigate to 'enable-authentication-confirmation'.
    // If unchecked (checked = false), navigate to 'disable-authentication-confirmation'.
    const pageUrl = `${checked ? 'enable' : 'disable'}-authentication-confirmation`;

    // navigate the page based on container type e.g. modal or page
    const url = redirectUrl || this.configService.reRouteIfModal(APP_ZONES.PROFILE, pageUrl);
    this.router.navigateByUrl(url);
  }

  private reset() {
    this.appPreferences = null;
  }

  public updateFlutterWebView(checked: boolean) {
    this.flutterWebView?.callHandler('biometricsPreference', checked);
  }

  public listenForBiometricsSetupResult() {
    window.addEventListener(
      'biometricsPreferenceState',
      (event: any) => {
        this.isChecked = event.detail;
        updateBiometricsFlagInStorage(this.isChecked);
      },
      false,
    );
  }
}
